import { Callout } from '@carouselgg/nexus/callout'
import type { GameTrustScore, TrustScore } from '@carouselgg/auth/types'
import type { TrustStanding } from '../../api/compete'
import { TrustHero } from './TrustHero'
import { TrustSection } from './TrustSection'
import { TipList } from './TipList'
import { CategoryBreakdown } from './CategoryBreakdown'
import { LowScoreNotice } from './LowScoreNotice'
import { BASELINE_CATEGORIES, GAME_CATEGORIES, biggestFirst, gameCategoryCopy, gameShortName, isLagging } from './trustCopy'
import styles from './TrustPage.module.css'

export type StandingState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; standing: TrustStanding }

/**
 * One game's score: what that game's competitions check (or will, once they
 * check per game), how to raise it, and the split between the game's own
 * history and the baseline every game shares.
 */
export function GameView({ game, overall, standing }: {
  game: GameTrustScore
  overall: TrustScore
  standing: StandingState
}) {
  const short = gameShortName(game)
  const score = game.score
  const ready = standing.status === 'ready' ? standing.standing : null
  const lagging = isLagging(game, overall) || ready?.low_game_specific === true
  const tips = biggestFirst(ready?.tips)
  const tipTotal = tips.reduce((sum, t) => sum + t.points, 0)
  const total = [...GAME_CATEGORIES, ...BASELINE_CATEGORIES].reduce((sum, k) => sum + (score.categories[k]?.max ?? 0), 0)

  return (
    <>
      <TrustHero score={score.score} level={score.level} eyebrow={`Your ${game.game_name} score`}>
        <p>
          {ready?.scope === 'game' && <>This is the number <b>{short} competitions check</b> when you sign up. </>}
          It starts from your baseline (identity, social, account age) and adds only your {short} history.
          {ready?.scope === 'overall' && <> For now, {short} competitions check <b>your Carousel score</b> ({overall.score}) when you sign up.</>}
        </p>
      </TrustHero>

      {lagging && (
        <Callout variant="warning" icon>
          <div className={styles.calloutStack}>
            <b>Well verified overall, new to {short} here</b>
            <span>
              Your Carousel score is {overall.score}, but you have little {short} history on Carousel yet.
              Ranked {short} games and a {short} season close the gap.
            </span>
          </div>
        </Callout>
      )}

      {ready?.scope === 'game' && <LowScoreNotice score={score.score} subject={`your ${short} score`} />}

      <TrustSection title={`Raise your ${short} score`} aside={tips.length > 0 ? `Up to ${tipTotal} more points` : undefined}>
        {standing.status === 'loading' && <p className={styles.quiet}>Loading ways to raise it…</p>}
        {standing.status === 'error' && <Callout variant="error" icon>{standing.message}</Callout>}
        {ready && tips.length > 0 && <TipList tips={tips} label={`Ways to raise your ${short} score`} />}
        {ready && tips.length === 0 && <p className={styles.quiet}>Nothing left to add for now. Keep playing and it keeps counting.</p>}
      </TrustSection>

      <TrustSection title={`What makes up your ${score.score}`} aside={`Out of ${total}`}>
        <h3 className={styles.groupLabel}>{game.game_name} only</h3>
        <CategoryBreakdown
          label={`${game.game_name} only`}
          categories={score.categories}
          keys={GAME_CATEGORIES}
          copyFor={(key) => gameCategoryCopy(key, short)}
        />
        <h3 className={styles.groupLabel}>Your baseline · shared with every game</h3>
        <CategoryBreakdown
          label="Your baseline"
          categories={score.categories}
          keys={BASELINE_CATEGORIES}
          copyFor={(key) => gameCategoryCopy(key, short)}
          openFirst={false}
        />
      </TrustSection>
    </>
  )
}
